import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CATEGORIES } from "../lib/categories";
import { Plus, Edit3, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import AutomationCenter from "./AutomationCenter";
import RbacAdmin from "./RbacAdmin";

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("products");

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20 text-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/account" replace />;

  return (
    <div className="bg-white" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="rh-overline mb-3">Operator console</div>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mb-8">Admin</h1>

        <div className="border-b border-[#0a0a0a] flex gap-1 mb-8 overflow-x-auto">
          {["products", "orders", "coupons", "inventory", "automation", "access"].map((t) => (
            <button
              key={t}
              data-testid={`tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-5 py-3 text-sm font-semibold uppercase tracking-widest -mb-px border-b-2 whitespace-nowrap ${tab === t ? "border-[#002fa7] text-[#002fa7]" : "border-transparent text-[#4a4a4a]"}`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "products" ? <ProductsAdmin /> : tab === "orders" ? <OrdersAdmin /> : tab === "coupons" ? <CouponsAdmin /> : tab === "inventory" ? <InventoryAdmin /> : tab === "automation" ? <AutomationCenter /> : <RbacAdmin />}
      </div>
    </div>
  );
}

const emptyForm = { name: "", description: "", brand: "", category: "consumer-electronics", price: 0, image: "", images_csv: "", options_json: "", rating: 4.5, reviews: 0, stock: 100, featured: false };

function ProductsAdmin() {
  const qc = useQueryClient();
  const { data: products = [] } = useQuery({ queryKey: ["admin", "products"], queryFn: async () => (await api.get("/products", { params: { limit: 200 } })).data });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const openNew = () => { setEditing("new"); setForm(emptyForm); };
  const openEdit = (p) => {
    setEditing(p.product_id);
    setForm({
      ...emptyForm, ...p,
      images_csv: (p.images || []).join(", "),
      options_json: p.options && p.options.length ? JSON.stringify(p.options) : "",
    });
  };

  const save = useMutation({
    mutationFn: async () => {
      let options = [];
      if (form.options_json && form.options_json.trim()) {
        try { options = JSON.parse(form.options_json); }
        catch { throw new Error("Options must be valid JSON, e.g. [{\"name\":\"Color\",\"values\":[\"Black\",\"White\"]}]"); }
      }
      const images = form.images_csv ? form.images_csv.split(",").map((s) => s.trim()).filter(Boolean) : [];
      const payload = {
        name: form.name, description: form.description, brand: form.brand, category: form.category,
        price: parseFloat(form.price), image: form.image, images, options,
        rating: parseFloat(form.rating), reviews: parseInt(form.reviews || 0),
        stock: parseInt(form.stock || 0), featured: !!form.featured,
      };
      if (editing === "new") return (await api.post("/products", payload)).data;
      return (await api.put(`/products/${editing}`, payload)).data;
    },
    onSuccess: () => { toast.success("Saved"); setEditing(null); qc.invalidateQueries({ queryKey: ["admin", "products"] }); qc.invalidateQueries({ queryKey: ["products"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const del = useMutation({
    mutationFn: async (pid) => (await api.delete(`/products/${pid}`)).data,
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["admin", "products"] }); qc.invalidateQueries({ queryKey: ["products"] }); },
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="text-sm text-[#4a4a4a]">{products.length} products</div>
        <button data-testid="add-product-btn" onClick={openNew} className="rh-btn-primary inline-flex items-center gap-2"><Plus size={14} /> New product</button>
      </div>

      <div className="border border-[#e5e5e5]">
        {products.map((p) => (
          <div key={p.product_id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#e5e5e5] items-center" data-testid={`admin-prod-${p.product_id}`}>
            <div className="col-span-1"><div className="w-12 h-12 bg-[#f5f5f5]"><img src={p.image} alt="" className="w-full h-full object-cover" /></div></div>
            <div className="col-span-5">
              <div className="font-semibold text-sm line-clamp-1">{p.name}</div>
              <div className="text-xs text-[#4a4a4a] font-mono">{p.brand} · {p.category}</div>
            </div>
            <div className="col-span-2 font-mono text-sm">${p.price.toFixed(2)}</div>
            <div className="col-span-2 font-mono text-xs text-[#4a4a4a]">Stock: {p.stock}</div>
            <div className="col-span-2 flex gap-2 justify-end">
              <button onClick={() => openEdit(p)} className="p-2 hover:bg-[#f5f5f5]" data-testid={`edit-${p.product_id}`}><Edit3 size={14} /></button>
              <button onClick={() => { if (confirm("Delete this product?")) del.mutate(p.product_id); }} className="p-2 hover:bg-[#ff3b30] hover:text-white" data-testid={`del-${p.product_id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white border border-[#0a0a0a] w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center px-6 py-4 border-b border-[#0a0a0a]">
              <h3 className="font-display font-bold text-xl">{editing === "new" ? "New product" : "Edit product"}</h3>
              <button onClick={() => setEditing(null)} className="p-2"><X size={18} /></button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="p-6 grid sm:grid-cols-2 gap-4">
              <AField label="Name" className="sm:col-span-2" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
              <AField label="Brand" value={form.brand} onChange={(v) => setForm((f) => ({ ...f, brand: v }))} />
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-[#4a4a4a] mb-2">Category</span>
                <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="w-full border border-[#0a0a0a] px-3 py-2 text-sm bg-white">
                  {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </label>
              <AField label="Price ($)" type="number" step="0.01" value={form.price} onChange={(v) => setForm((f) => ({ ...f, price: v }))} />
              <AField label="Stock" type="number" value={form.stock} onChange={(v) => setForm((f) => ({ ...f, stock: v }))} />
              <AField label="Rating" type="number" step="0.1" value={form.rating} onChange={(v) => setForm((f) => ({ ...f, rating: v }))} />
              <AField label="Reviews" type="number" value={form.reviews} onChange={(v) => setForm((f) => ({ ...f, reviews: v }))} />
              <AField label="Image URL (primary)" className="sm:col-span-2" value={form.image} onChange={(v) => setForm((f) => ({ ...f, image: v }))} />
              <label className="block sm:col-span-2">
                <span className="block text-xs uppercase tracking-widest text-[#4a4a4a] mb-2">Gallery image URLs (comma-separated, optional)</span>
                <textarea value={form.images_csv} onChange={(e) => setForm((f) => ({ ...f, images_csv: e.target.value }))} rows={2} className="w-full border border-[#0a0a0a] px-3 py-2 text-sm" />
              </label>
              <label className="block sm:col-span-2">
                <span className="block text-xs uppercase tracking-widest text-[#4a4a4a] mb-2">Variant options (JSON, optional). Example: [{`{"name":"Color","values":["Black","White"]}`}]</span>
                <textarea value={form.options_json} onChange={(e) => setForm((f) => ({ ...f, options_json: e.target.value }))} rows={2} className="w-full border border-[#0a0a0a] px-3 py-2 text-sm font-mono" />
              </label>
              <label className="block sm:col-span-2">
                <span className="block text-xs uppercase tracking-widest text-[#4a4a4a] mb-2">Description</span>
                <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={3} className="w-full border border-[#0a0a0a] px-3 py-2 text-sm" />
              </label>
              <label className="flex items-center gap-2 sm:col-span-2">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} />
                <span className="text-sm">Featured on homepage</span>
              </label>
              <div className="sm:col-span-2 flex gap-3 justify-end pt-2 border-t border-[#e5e5e5]">
                <button type="button" onClick={() => setEditing(null)} className="rh-btn-secondary">Cancel</button>
                <button type="submit" disabled={save.isPending} data-testid="save-product-btn" className="rh-btn-primary">{save.isPending ? "Saving…" : "Save"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function OrdersAdmin() {
  const qc = useQueryClient();
  const { data: orders = [] } = useQuery({ queryKey: ["admin", "orders"], queryFn: async () => (await api.get("/admin/orders")).data });
  const update = useMutation({
    mutationFn: async ({ id, status }) => (await api.put(`/admin/orders/${id}/status`, { status })).data,
    onSuccess: () => { toast.success("Status updated"); qc.invalidateQueries({ queryKey: ["admin", "orders"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  return (
    <div>
      <div className="text-sm text-[#4a4a4a] mb-6">{orders.length} orders</div>
      <div className="border border-[#e5e5e5]">
        {orders.length === 0 ? (
          <div className="p-10 text-center text-[#4a4a4a]">No orders yet.</div>
        ) : (
          orders.map((o) => (
            <div key={o.order_id} className="grid grid-cols-12 gap-3 px-4 py-4 border-b border-[#e5e5e5] items-center" data-testid={`admin-order-${o.order_id}`}>
              <div className="col-span-3"><div className="font-mono text-sm font-semibold">{o.order_id}</div><div className="text-xs text-[#4a4a4a]">{new Date(o.created_at).toLocaleString()}</div></div>
              <div className="col-span-3"><div className="text-sm">{o.shipping.full_name}</div><div className="text-xs text-[#4a4a4a]">{o.shipping.email}</div></div>
              <div className="col-span-2 font-mono text-sm">${o.total.toFixed(2)}</div>
              <div className="col-span-2 text-xs">{o.items.length} item{o.items.length !== 1 ? "s" : ""}</div>
              <div className="col-span-2">
                <select value={o.status} onChange={(e) => update.mutate({ id: o.order_id, status: e.target.value })} className="w-full border border-[#0a0a0a] px-2 py-1 text-xs bg-white" data-testid={`status-${o.order_id}`}>
                  {["pending", "processing", "shipped", "delivered", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function AField({ label, type = "text", step, value, onChange, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs uppercase tracking-widest text-[#4a4a4a] mb-2">{label}</span>
      <input type={type} step={step} value={value} onChange={(e) => onChange(e.target.value)} required className="w-full border border-[#0a0a0a] px-3 py-2 text-sm bg-white" />
    </label>
  );
}

function CouponsAdmin() {
  const qc = useQueryClient();
  const { data: coupons = [] } = useQuery({ queryKey: ["admin", "coupons"], queryFn: async () => (await api.get("/coupons")).data });
  const [form, setForm] = useState({ code: "", type: "percent", value: 10, min_subtotal: 0, active: true });
  const create = useMutation({
    mutationFn: async () => (await api.post("/coupons", { ...form, value: parseFloat(form.value), min_subtotal: parseFloat(form.min_subtotal) })).data,
    onSuccess: () => { toast.success("Coupon created"); setForm({ code: "", type: "percent", value: 10, min_subtotal: 0, active: true }); qc.invalidateQueries({ queryKey: ["admin", "coupons"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const del = useMutation({
    mutationFn: async (code) => (await api.delete(`/coupons/${code}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "coupons"] }),
  });
  return (
    <div>
      <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }} className="border border-[#0a0a0a] p-5 mb-6 grid sm:grid-cols-5 gap-3 items-end" data-testid="new-coupon-form">
        <label className="block"><span className="block text-xs uppercase tracking-widest text-[#4a4a4a] mb-2">Code</span><input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="w-full border border-[#0a0a0a] px-3 py-2 text-sm" data-testid="coupon-code" /></label>
        <label className="block"><span className="block text-xs uppercase tracking-widest text-[#4a4a4a] mb-2">Type</span>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full border border-[#0a0a0a] px-3 py-2 text-sm bg-white"><option value="percent">% Percent</option><option value="amount">$ Amount</option></select>
        </label>
        <AField label="Value" type="number" step="0.01" value={form.value} onChange={(v) => setForm({ ...form, value: v })} />
        <AField label="Min subtotal" type="number" step="0.01" value={form.min_subtotal} onChange={(v) => setForm({ ...form, min_subtotal: v })} />
        <button type="submit" className="rh-btn-primary" data-testid="create-coupon-btn">Create</button>
      </form>
      <div className="border border-[#e5e5e5]">
        {coupons.length === 0 ? <div className="p-8 text-center text-[#4a4a4a]">No coupons yet.</div> :
          coupons.map((c) => (
            <div key={c.code} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#e5e5e5] items-center" data-testid={`coupon-${c.code}`}>
              <div className="col-span-3 font-mono font-semibold">{c.code}</div>
              <div className="col-span-3 text-sm">{c.type === "percent" ? `${c.value}% off` : `$${c.value.toFixed(2)} off`}</div>
              <div className="col-span-3 text-sm text-[#4a4a4a]">Min ${c.min_subtotal.toFixed(2)}</div>
              <div className="col-span-2 text-xs">{c.active ? "Active" : "Inactive"}</div>
              <div className="col-span-1 text-right"><button onClick={() => del.mutate(c.code)} className="p-2 hover:bg-[#ff3b30] hover:text-white" data-testid={`del-coupon-${c.code}`}><Trash2 size={14} /></button></div>
            </div>
          ))
        }
      </div>
    </div>
  );
}

function InventoryAdmin() {
  const { data } = useQuery({ queryKey: ["admin", "inventory"], queryFn: async () => (await api.get("/admin/inventory")).data });
  if (!data) return <div className="text-[#4a4a4a]">Loading…</div>;
  return (
    <div data-testid="inventory-dashboard">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {[
          ["Total products", data.total_products],
          ["Total units", data.total_units],
          ["Inventory value", `$${data.total_inventory_value.toLocaleString()}`],
          ["Low / Out of stock", `${data.low_stock_count} / ${data.out_of_stock_count}`],
        ].map(([t, v]) => (
          <div key={t} className="border border-[#0a0a0a] p-5">
            <div className="rh-overline">{t}</div>
            <div className="font-display font-black text-3xl rh-num mt-2">{v}</div>
          </div>
        ))}
      </div>

      <h3 className="font-display font-bold text-xl mb-3">By category</h3>
      <div className="border border-[#e5e5e5] mb-8">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 border-b border-[#e5e5e5] bg-[#f5f5f5] text-xs font-mono uppercase tracking-widest text-[#4a4a4a]">
          <div className="col-span-5">Category</div><div className="col-span-3">SKUs</div><div className="col-span-2">Units</div><div className="col-span-2 text-right">Value</div>
        </div>
        {data.by_category.map((c) => (
          <div key={c.category} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#e5e5e5] text-sm">
            <div className="col-span-5">{c.category}</div><div className="col-span-3 rh-num">{c.count}</div><div className="col-span-2 rh-num">{c.units}</div><div className="col-span-2 text-right rh-num">${c.value.toLocaleString()}</div>
          </div>
        ))}
      </div>

      {data.low_stock.length > 0 && (
        <>
          <h3 className="font-display font-bold text-xl mb-3 text-[#ff3b30]">Low stock alerts</h3>
          <div className="border border-[#ff3b30] mb-8" data-testid="low-stock-list">
            {data.low_stock.map((p) => (
              <div key={p.product_id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#e5e5e5] text-sm">
                <div className="col-span-9">{p.name}</div>
                <div className="col-span-3 text-right rh-num text-[#ff3b30] font-bold">{p.stock} left</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
